import React, { useEffect, useMemo, useRef, useState } from "react";
import { ISurveyQuestion } from "../../../models/survey/survey-question.model";
import {
    custommap,
    custommapStack,
    custommapObject,
    custommapOverlay,
    custommapError,
} from "./survey-answer-question-card.module.scss";

export interface ISurveyAnswerCustomMapProps {
    question: ISurveyQuestion;
    value: string | number | Array<string | number> | null;
    onChange: (value: string | number | Array<string | number>) => void;
    onSave?: (value: string | number | Array<string | number>, shouldClose?: boolean) => void;
    svgPath: string;
    mapConfig: IMapConfig;
    ariaLabel?: string;
}

export interface IMapConfig {
    // Typ mapy: "id" (znajduje po id) lub "label" (znajduje po inkscape:label)
    type: "id" | "label";
    // Dane regionów - dla typu "id" to tablica z { key, id }, dla "label" to Record<string, string>
    regions: IMapRegion[] | Record<string, string>;
    // Prefiks dla label (np. "skirt_len_" dla skirt-length)
    labelPrefix?: string;
    // Klucz specjalnego regionu, który nie jest klikalny (np. "base" dla skirt)
    nonClickableKey?: string;
    // Style CSS dla SVG
    svgStyle?: {
        fill?: string;
        stroke?: string;
        strokeWidth?: number;
    };
    // Dodatkowe style CSS jako string
    additionalStyle?: string;
}

export interface IMapRegion {
    key: string;
    id: string | string[]; // Może być pojedyncze id lub tablica id (dla wielu elementów SVG w jednym regionie)
    label?: string;
}

interface IRegionWithOptionId {
    key: string;
    id?: string | string[];
    label: string;
    optionId: string | number | null;
}

const SurveyAnswerCustomMap: React.FC<ISurveyAnswerCustomMapProps> = ({
    question,
    value,
    onChange,
    onSave,
    svgPath,
    mapConfig,
    ariaLabel = "Mapa",
}) => {
    const objectRef = useRef<HTMLObjectElement>(null);
    const overlayRef = useRef<SVGSVGElement>(null);
    const errorRef = useRef<HTMLParagraphElement>(null);
    const regionsRef = useRef<Element[]>([]);
    const [regions, setRegions] = useState<Element[]>([]);
    const [isLoaded, setIsLoaded] = useState(false);

    // Przygotuj dane regionów z optionId
    const regionsWithOptionId = useMemo<IRegionWithOptionId[]>(() => {
        const options = Array.isArray(question.options) ? question.options : [];
        
        let regionsList: IMapRegion[] = [];
        
        if (mapConfig.type === "id") {
            // Dla typu "id" - regions to tablica IMapRegion[]
            regionsList = mapConfig.regions as IMapRegion[];
        } else {
            // Dla typu "label" - regions to Record<string, string>
            const labels = mapConfig.regions as Record<string, string>;
            regionsList = Object.keys(labels).map(key => ({
                key,
                id: key, // Tymczasowe, będzie używane do matchowania
                label: labels[key],
            }));
        }

        if (options.length === 0) {
            return regionsList.map(region => ({
                key: region.key,
                id: region.id,
                label: region.label || region.key,
                optionId: null,
            }));
        }

        return regionsList.map(region => {
            const label = region.label || region.key;
            // Dopasuj po label z options
            const matchingOption = options.find(option => {
                if (!option.label) return false;
                const optionLabelUpper = option.label.toUpperCase();
                const labelUpper = label.toUpperCase();
                return optionLabelUpper === labelUpper || 
                       optionLabelUpper.includes(labelUpper) ||
                       labelUpper.includes(optionLabelUpper);
            });
            
            const optionId = matchingOption 
                ? (matchingOption.id ?? matchingOption.value ?? null)
                : null;

            return {
                key: region.key,
                id: region.id,
                label,
                optionId,
            };
        });
    }, [question.options, mapConfig]);

    const showError = (msg: string) => {
        if (errorRef.current) {
            errorRef.current.hidden = false;
            errorRef.current.textContent = msg;
        }
    };

    const hideError = () => {
        if (errorRef.current) {
            errorRef.current.hidden = true;
            errorRef.current.textContent = "";
        }
    };

    const updateSummary = () => {
        const currentRegions = overlayRef.current?.querySelectorAll(".body-region") || [];
        console.log("0. custom map => currentRegions", currentRegions);
        const activeOptionIds = Array.from(currentRegions)
            .filter((r) => {
                // Pomiń nieklikalne regiony
                if (mapConfig.nonClickableKey) {
                    const key = r.getAttribute("data-key");
                    if (key === mapConfig.nonClickableKey) return false;
                }
                return r.classList.contains("is-active");
            })
            .map((r) => {
                const optionId = r.getAttribute("data-option-id");
                return optionId ? (isNaN(Number(optionId)) ? optionId : Number(optionId)) : null;
            })
            .filter((id): id is string | number => id !== null);
        
        console.log("0. custom map => activeOptionIds", activeOptionIds);
        // Usuń duplikaty
        const uniqueOptionIds = Array.from(
            new Map(
                activeOptionIds.map(id => [String(id), id])
            ).values()
        );
        console.log("0. custom map => uniqueOptionIds", uniqueOptionIds);
        if (onSave) {
            // Zawsze zwracaj tablicę
            console.log("body parts => uniqueOptionIds", uniqueOptionIds);
            onChange(uniqueOptionIds);
        }
    };

    const toggleActive = (identifier: string | number) => {
        const currentRegions = overlayRef.current?.querySelectorAll(".body-region") || [];
        
        if (mapConfig.type === "id") {
            // Dla typu "id" - toggle po optionId
            const optionIdStr = String(identifier);
            currentRegions.forEach((r) => {
                const rOptionId = r.getAttribute("data-option-id");
                if (rOptionId === optionIdStr) {
                    r.classList.toggle("is-active");
                }
            });
        } else {
            // Dla typu "label" - toggle po key
            const key = String(identifier);
            if (key === mapConfig.nonClickableKey) return;
            
            currentRegions.forEach((r) => {
                const rKey = r.getAttribute("data-key");
                if (rKey === key) {
                    r.classList.toggle("is-active");
                }
            });
        }
        
        updateSummary();
    };

    const injectSvgStyle = (doc: Document, svgRoot: SVGElement) => {
        const style = doc.createElementNS("http://www.w3.org/2000/svg", "style");
        style.setAttribute("type", "text/css");
        
        const fill = mapConfig.svgStyle?.fill ?? "#ffffff";
        const stroke = mapConfig.svgStyle?.stroke ?? "rgba(42, 30, 47, 0.42)";
        const strokeWidth = mapConfig.svgStyle?.strokeWidth ?? 2;
        
        let styleText = `
            svg { 
                width: 100% !important; 
                height: 100% !important; 
                max-width: 100% !important;
                max-height: 100% !important;
            }
            path, rect, circle, ellipse, polygon, polyline {
                fill: ${fill} !important;
                stroke: ${stroke} !important;
                stroke-width: ${strokeWidth} !important;
                stroke-linecap: round;
                stroke-linejoin: round;
                vector-effect: non-scaling-stroke;
            }
        `;
        
        if (mapConfig.additionalStyle) {
            styleText += mapConfig.additionalStyle;
        }
        
        style.textContent = styleText;
        svgRoot.insertBefore(style, svgRoot.firstChild);
    };

    const applyInitialSelection = () => {
        const currentRegions = overlayRef.current?.querySelectorAll(".body-region") || [];
        
        const optionIdsToSelect: (string | number)[] = Array.isArray(value) 
            ? value
            : value !== null && value !== undefined
                ? [value]
                : [];
        
        currentRegions.forEach((r) => {
            const rOptionId = r.getAttribute("data-option-id");
            const rKey = r.getAttribute("data-key");
            
            // Ustaw nieklikalne regiony jako zawsze aktywne
            if (mapConfig.nonClickableKey && rKey === mapConfig.nonClickableKey) {
                r.classList.add("is-active");
                return;
            }
            
            if (rOptionId) {
                const rOptionIdNum = isNaN(Number(rOptionId)) ? rOptionId : Number(rOptionId);
                const isSelected = optionIdsToSelect.some(optionId => {
                    const optionIdNum = typeof optionId === 'string' && !isNaN(Number(optionId)) 
                        ? Number(optionId) 
                        : optionId;
                    return rOptionIdNum === optionIdNum || String(rOptionIdNum) === String(optionIdNum);
                });
                
                if (isSelected) {
                    r.classList.add("is-active");
                } else {
                    r.classList.remove("is-active");
                }
            }
        });
    };

    const buildOverlay = () => {
        hideError();
        const objectEl = objectRef.current;
        const overlay = overlayRef.current;
        
        if (!objectEl || !overlay) return;

        const doc = objectEl.contentDocument;
        if (!doc) {
            showError("Nie mogę odczytać SVG. Otwórz stronę przez lokalny serwer (np. Live Server / http://).");
            return;
        }

        const svgRoot = doc.querySelector("svg");
        if (!svgRoot) {
            showError("W pliku SVG nie znaleziono elementu <svg>.");
            return;
        }

        const viewBox = svgRoot.getAttribute("viewBox");
        
        // Usuń atrybuty width/height z SVG, aby CSS mógł kontrolować rozmiar
        svgRoot.removeAttribute("width");
        svgRoot.removeAttribute("height");
        
        if (viewBox) {
            overlay.setAttribute("viewBox", viewBox);
        } else {
            overlay.setAttribute("width", "100%");
            overlay.setAttribute("height", "100%");
        }
        svgRoot.setAttribute("preserveAspectRatio", "xMidYMid meet");
        overlay.setAttribute("preserveAspectRatio", "xMidYMid meet");

        injectSvgStyle(doc, svgRoot);

        overlay.innerHTML = "";
        const overlayGroup = document.createElementNS("http://www.w3.org/2000/svg", "g");
        const srcGroup = svgRoot.querySelector("g[transform]") || svgRoot.querySelector("g");
        const groupTransform = srcGroup?.getAttribute("transform");
        if (groupTransform) overlayGroup.setAttribute("transform", groupTransform);
        overlay.appendChild(overlayGroup);

        if (mapConfig.type === "id") {
            // Dla typu "id" - znajdź elementy po id (może być pojedyncze id lub tablica)
            regionsWithOptionId.forEach((region) => {
                if (!region.id || region.optionId === null) return;
                
                const ids = Array.isArray(region.id) ? region.id : [region.id];
                
                ids.forEach((id) => {
                    const src = doc.getElementById(id);
                    if (!src) return;
                    
                    const clone = src.cloneNode(true) as SVGElement;
                    clone.removeAttribute("style");
                    clone.removeAttribute("fill");
                    clone.removeAttribute("stroke");
                    clone.setAttribute("class", "body-region");
                    clone.setAttribute("data-option-id", String(region.optionId));
                    clone.setAttribute("data-key", region.key);
                    clone.setAttribute("pointer-events", "all");
                    overlayGroup.appendChild(clone);
                });
            });
        } else {
            // Dla typu "label" - znajdź elementy po inkscape:label
            const allNodes = Array.from(doc.querySelectorAll("*"));
            allNodes.forEach((src) => {
                const labelAttr =
                    src.getAttribute("id") ||
                    src.getAttribute("inkscape:label") ||
                    src.getAttributeNS("http://www.inkscape.org/namespaces/inkscape", "label") ||
                    "";
                
                if (!labelAttr) return;
                
                let key: string | null = null;
                let optionId: string | number | null = null;
                
                const prefix = mapConfig.labelPrefix || "";
                
                // Sprawdź najpierw czy to base region (jeśli jest zdefiniowany)
                // Base region ma format: prefix.replace("_len_", "_base_") lub prefix.replace("len_", "base_")
                if (mapConfig.nonClickableKey) {
                    const basePrefix = prefix.replace(/_len_/, "_base_").replace(/len_/, "base_");
                    const baseExact = prefix.replace(/_len_/, "_base").replace(/len_/, "base");
                    if (labelAttr.startsWith(basePrefix) || labelAttr === baseExact) {
                        key = mapConfig.nonClickableKey;
                    }
                }
                
                // Jeśli nie base, sprawdź czy to normalny region
                if (!key && labelAttr.startsWith(prefix)) {
                    // Wyciągnij klucz z label (np. "skirt_len_mini_" -> "mini")
                    const match = labelAttr.match(new RegExp(`^${prefix.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}([a-z]+)_`));
                    if (match) {
                        key = match[1];
                        const region = regionsWithOptionId.find(r => r.key === key);
                        if (region && region.optionId !== null) {
                            optionId = region.optionId;
                        }
                    }
                }
                
                if (!key) return;
                
                const clone = src.cloneNode(true) as SVGElement;
                clone.removeAttribute("style");
                clone.removeAttribute("fill");
                clone.removeAttribute("stroke");
                clone.setAttribute("pointer-events", "all");
                
                const baseClass = key === mapConfig.nonClickableKey ? "body-region skirt-base" : "body-region";
                clone.setAttribute("class", baseClass);
                clone.setAttribute("data-key", key);
                
                if (optionId !== null) {
                    clone.setAttribute("data-option-id", String(optionId));
                }
                
                const region = regionsWithOptionId.find(r => r.key === key);
                const label = region?.label || key;
                clone.setAttribute("data-value", label);
                
                // Nieklikalne regiony zawsze aktywne
                if (key === mapConfig.nonClickableKey) {
                    clone.classList.add("is-active");
                }
                
                overlayGroup.appendChild(clone);
            });
        }

        const newRegions = Array.from(overlay.querySelectorAll(".body-region"));
        regionsRef.current = newRegions;
        setRegions(newRegions);

        if (newRegions.length === 0) {
            showError(`Nie znaleziono elementów w pliku SVG.`);
        }

        // Delegacja zdarzeń
        const handleOverlayClick = (e: MouseEvent) => {
            const target = e.target as Element;
            if (!target) return;
            
            let region: Element | null = null;
            
            if (target.classList && target.classList.contains("body-region")) {
                region = target;
            } else {
                let parent = target.parentNode;
                while (parent && parent !== overlay) {
                    if (parent instanceof Element && parent.classList && parent.classList.contains("body-region")) {
                        region = parent;
                        break;
                    }
                    parent = parent.parentNode;
                }
            }
            
            if (!region) return;
            
            e.stopPropagation();
            
            if (mapConfig.type === "id") {
                const optionId = region.getAttribute("data-option-id");
                if (optionId) {
                    const optionIdValue = isNaN(Number(optionId)) ? optionId : Number(optionId);
                    toggleActive(optionIdValue);
                }
            } else {
                const key = region.getAttribute("data-key");
                if (key && key !== mapConfig.nonClickableKey) {
                    toggleActive(key);
                }
            }
        };

        overlay.addEventListener("click", handleOverlayClick);

        applyInitialSelection();
        setIsLoaded(true);
        
        return () => {
            overlay.removeEventListener("click", handleOverlayClick);
        };
    };

    useEffect(() => {
        const objectEl = objectRef.current;
        if (!objectEl) return;

        let cleanup: (() => void) | undefined;

        const handleLoad = () => {
            setTimeout(() => {
                cleanup = buildOverlay();
            }, 50);
        };

        objectEl.addEventListener("load", handleLoad);
        
        if (objectEl.contentDocument && objectEl.contentDocument.querySelector("svg")) {
            setTimeout(() => {
                cleanup = buildOverlay();
            }, 50);
        }

        return () => {
            objectEl.removeEventListener("load", handleLoad);
            if (cleanup) {
                cleanup();
            }
        };
    }, [regionsWithOptionId]);

    useEffect(() => {
        if (isLoaded && regions.length > 0) {
            applyInitialSelection();
        }
    }, [value, isLoaded, regions.length]);

    return (
        <div className={custommap} aria-label={ariaLabel}>
            <div className={custommapStack}>
                <object
                    ref={objectRef}
                    className={custommapObject}
                    type="image/svg+xml"
                    data={svgPath}
                    aria-label={ariaLabel}
                />
                <svg
                    ref={overlayRef}
                    className={custommapOverlay}
                    aria-hidden="true"
                />
            </div>
            <p ref={errorRef} className={custommapError} hidden></p>
        </div>
    );
};

export default SurveyAnswerCustomMap;
